import React, { useMemo, useState } from "react";
import {
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import Alerts from "../../molecules/Alerts";
import { Controller, useForm, useWatch } from "react-hook-form";
import { EmailSelect } from "../../organisms/EmailSelect";
import { zodResolver } from "@hookform/resolvers/zod";
import FormInput from "../../molecules/FormInput";
import {
  submitRequest,
  financeApproverDataLoader,
  lineManagerDataLoader,
  getEmployeesFromMyHR,
} from "../../../utils/queries";
import {
  requestPortalForm,
  schema,
  submitData,
} from "../../../utils/common";
import { debouncePromise } from "../../../utils/debouncePromise";
import AsyncSelect from "react-select/async";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { CountrySelect } from "../../organisms/CountrySelect";
import ControlledSelect from "../../organisms/ControlledSelect";
import useGetLineManager from "../../../hooks/useGetLineManager";
import { useGetCountry } from "../../../hooks/useGetCountry";
import useGetCurrency from "../../../hooks/useGetCurrency";
import Select from "react-select";
import AddAlert from "../../atoms/AddAlert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faPhone,
  faRoute,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import "./HomePage.css";

/* ── Reusable Section Card ── */
const SectionCard = ({ icon, title, subtitle, children }) => (
  <div className="hp-section-card">
    <div className="hp-section-card__header">
      <div className="hp-section-card__title">
        <FontAwesomeIcon icon={icon} className="hp-section-card__icon" />
        <div>
          <span>{title}</span>
          {subtitle && <small className="hp-section-card__subtitle">{subtitle}</small>}
        </div>
      </div>
    </div>
    <div className="hp-section-card__body">{children}</div>
  </div>
);

const HomePage = () => {
  const { alerts, setAlerts, setAlert, clearAlerts } = AddAlert();
  // Incremented each time a MyHR employee is auto-filled so the PhoneInput
  // remounts and onMount fires with the auto-detected country dial code.
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  const {
    clearErrors,
    setValue,
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: requestPortalForm,
    mode: "all",
  });
  const sendFormLinkTo = useWatch({ control, name: "sendFormLinkTo" });
  const selectedAccount = useWatch({ control, name: "selectedAccount" });
  const isExistingEmployee = !!selectedAccount?.myhrid;
  const { data, isSuccess: isLineManagerLoaded } = useGetLineManager();
  const { options: countryOptions, isLoading: isCountryLoading } = useGetCountry(isLineManagerLoaded);
  const { options: currencyOptions, isLoading: isCurrencyLoading } = useGetCurrency();

  const onSubmit = async (request) => {
    clearAlerts();
    try {
      await submitRequest(submitData(request));
      setAlert(
        `Request created. ${request.employeeFirstName} ${request.employeeLastName} will receive a link by e-mail.`,
        "success",
        "submit",
        6000
      );
      reset();
    } catch (error) {
      const serverMsg =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null);
      let message;
      if (serverMsg && serverMsg.length < 300) {
        message = serverMsg;
      } else if (error?.message === "Network Error") {
        message =
          "We couldn't reach the request server. Check your connection and try again.";
      } else {
        message =
          "Something went wrong sending the request. Please try again or contact IT.";
      }
      setAlert(message, "danger", "submit");
    }
  };

  const onInvalid = (fieldErrors) => {
    const entries = Object.entries(fieldErrors).filter(([, e]) => e?.message);
    if (entries.length === 0) return;

    clearAlerts();
    const message = entries
      .map(([, err]) => err.message.replace(/\.+$/, ""))
      .join(". ") + ".";
    setAlert(message, "danger", "validation");

    const firstField = entries[0][0];
    const el = document.querySelector(
      `[name="${firstField}"], #${firstField}`
    );
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const getEmployeeFromMyHR = useMemo(
    () =>
      debouncePromise(async (query) => {
        if (!query || query.trim().length < 2) return [];
        try {
          return await getEmployeesFromMyHR(query);
        } catch (error) {
          setAlert(error.message, "danger");
          return [];
        }
      }, 300),
    [setAlert]
  );

  const autoFillEmployeeFields = async (selectedOption, onChange) => {
    if (selectedOption?.data) {
      setValue("employeeFirstName", selectedOption.data.firstName || "");
      setValue("employeeLastName", selectedOption.data.lastName || "");
      setValue("employeeEmail", selectedOption.data.email || "");
      setValue("employeePrivateEmail", selectedOption.data.privateEmail || "");
      setValue("mobilePhone", selectedOption.data.privatePhone || "");
      // Force PhoneInput to remount so onMount fires and captures the
      // auto-detected dial code for the pre-filled phone number.
      setPhoneInputKey((k) => k + 1);
      setValue("employeeEmail", selectedOption.data.email || "");
      setValue("employeeCountry", selectedOption.data.country || "");
      setValue("employeeAddressLine1", selectedOption.data.address1 || "");
      setValue("employeeAddressLine2", selectedOption.data.address2 || "");
      setValue("employeeCity", selectedOption.data.city || "");
      setValue("employeeZipCode", selectedOption.data.zipCode || "");
      onChange({
        id: null,
        statusCode: null,
        accountNumber: null,
        token: null,
        name: null,
        shortName: null,
        myhrid: selectedOption.data.userId,
        countryCode: null,
        label: null,
        value: null,
      });
    } else {
      setValue("employeeFirstName", "");
      setValue("employeeLastName", "");
      setValue("employeeEmail", "");
      setValue("employeePrivateEmail", "");
      setValue("mobilePhone", "");
      setValue("employeeAddressLine1", "");
      setValue("employeeAddressLine2", "");
      setValue("employeeCity", "");
      setValue("employeeZipCode", "");
      setValue("employeeCountry", "");
      onChange(null);
    }
  };

  return (
    <div className="hp-container">
      {/* ── Page Header ── */}
      <div className="hp-page-header">
        <div className="hp-page-header__inner">
          <h1 className="hp-page-header__title">Request form</h1>
          <p className="hp-page-header__subtitle">
            Submit a request to collect or update employee bank details. Fill in
            the sections below and click send. Fields marked with * are required.
          </p>
        </div>
      </div>

      <Alerts alerts={alerts} setAlerts={setAlerts} />

      <Form onSubmit={handleSubmit(onSubmit, onInvalid)} className="hp-form">
        {/* ═══ Employee Details Section ═══ */}
        <SectionCard
          icon={faUser}
          title="Employee Details"
          subtitle="Search for an existing employee by name or MyHR ID, or enter details manually"
        >
          <div className="hp-field-grid">
            <div className="hp-field hp-field--full">
              <ControlledSelect
                control={control}
                label={"Existing employee"}
                id="selectedAccount"
              >
                {({ value, onChange }) => (
                  <AsyncSelect
                    cacheOptions
                    loadOptions={getEmployeeFromMyHR}
                    onChange={(selectedOption) =>
                      autoFillEmployeeFields(selectedOption, onChange)
                    }
                    value={value}
                    isClearable
                  />
                )}
              </ControlledSelect>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="First name"
                  name="employeeFirstName"
                  control={control}
                  type="text"
                  required
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="Last name"
                  name="employeeLastName"
                  control={control}
                  type="text"
                  required
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="Address Line 1"
                  name="employeeAddressLine1"
                  control={control}
                  type="text"
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="Address Line 2"
                  name="employeeAddressLine2"
                  control={control}
                  type="text"
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="City"
                  name="employeeCity"
                  control={control}
                  type="text"
                />
              </FormGroup>
            </div>


            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="Zip Code"
                  name="employeeZipCode"
                  control={control}
                  type="text"
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="Business E-mail"
                  name="employeeEmail"
                  control={control}
                  type="email"
                  required={isExistingEmployee || sendFormLinkTo === "business"}
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <FormGroup>
                <FormInput
                  label="Personal E-mail"
                  name="employeePrivateEmail"
                  control={control}
                  type="email"
                  required={!isExistingEmployee || sendFormLinkTo === "personal"}
                />
              </FormGroup>
            </div>

            <div className="hp-field">
              <ControlledSelect control={control} label={"Country"} id="country" required>
                {({ value, onChange }) => (
                  <CountrySelect
                    value={value}
                    onChange={onChange}
                    className="react-select"
                    options={countryOptions}
                    isLoading={isCountryLoading}
                  />
                )}
              </ControlledSelect>
            </div>

            <div className="hp-field">
              <ControlledSelect
                control={control}
                label={"Currency"}
                id="currencyId"
                required
              >
                {({ value, onChange }) => (
                  <Select
                    options={currencyOptions}
                    isLoading={isCurrencyLoading}
                    value={currencyOptions.find((o) => o.value === value) ?? null}
                    onChange={(selected) => onChange(selected ? selected.value : null)}
                    isClearable
                    
                  />
                )}
              </ControlledSelect>
            </div>

          </div>
        </SectionCard>

        {/* ═══ Contact & Preferences Section ═══ */}
        <SectionCard
          icon={faPhone}
          title="Contact & Preferences"
          subtitle="Phone numbers and communication preferences"
        >
          <div className="hp-field-grid">
            <div className="hp-field">
              <ControlledSelect
                control={control}
                label={"Mobile Phone"}
                id="mobilePhone"
              >
                {({ value, onChange }) => (
                  <PhoneInput
                    key={phoneInputKey}
                    value={value}
                    name={"mobilePhone"}
                    inputClass="form-control"
                    inputStyle={{ width: "100%", paddingLeft: 48 }}
                    enableSearch
                    searchPlaceholder="Search countries"
                    disableSearchIcon
                    onMount={(_, countryData) => {
                      // Fires on (re)mount – captures the auto-detected dial code
                      // when the phone number is pre-filled from a MyHR search.
                      if (countryData?.dialCode) {
                        setValue("mobileCountryCallingCode", countryData.dialCode);
                      }
                    }}
                    onChange={(v, country) => {
                      onChange(v);
                      setValue("mobileCountryCallingCode", country?.dialCode || "");
                    }}
                    placeholder=""
                  />
                )}
              </ControlledSelect>
            </div>

            <div className="hp-field">
              <ControlledSelect
                control={control}
                label={"Business Phone"}
                id="businessPhone"
              >
                {({ value, onChange }) => (
                  <PhoneInput
                    value={value}
                    name={"businessPhone"}
                    inputClass="form-control"
                    inputStyle={{ width: "100%", paddingLeft: 48 }}
                    enableSearch
                    searchPlaceholder="Search countries"
                    disableSearchIcon
                    onChange={onChange}
                    placeholder=""
                  />
                )}
              </ControlledSelect>
            </div>


            <div className="hp-field hp-field--full">
              <div className="hp-preference-row hp-preference-row--inline">
                <span className="hp-preference-label">Send the MFA code by e-mail only</span>
                <Controller
                  name="vendorPassCodeByEmail"
                  control={control}
                  render={({ field }) => (
                    <label className="hp-toggle">
                      <input
                        type="checkbox"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <span className="hp-toggle__track">
                        <span className="hp-toggle__thumb" />
                      </span>
                    </label>
                  )}
                />
              </div>
            </div>

            <div className="hp-field hp-field--full">
              <div className="hp-preference-row hp-preference-row--inline">
                <span className="hp-preference-label">Send form link to</span>
                <Controller
                  name="sendFormLinkTo"
                  control={control}
                  render={({ field }) => (
                    <div className="hp-segment-control">
                      <button
                        type="button"
                        className={`hp-segment-btn${field.value === "personal" ? " hp-segment-btn--active" : ""}`}
                        onClick={() => field.onChange("personal")}
                      >
                        Personal e-mail
                      </button>
                      <button
                        type="button"
                        className={`hp-segment-btn${field.value === "business" ? " hp-segment-btn--active" : ""}`}
                        onClick={() => field.onChange("business")}
                      >
                        Business e-mail
                      </button>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ═══ Notification & Approval Section ═══ */}
        <SectionCard
          icon={faRoute}
          title="Approval"
          subtitle="Select who should be notified and approve this request"
        >
          <div className="hp-field-grid">
            <div className="hp-field">
              <ControlledSelect
                control={control}
                label={"Line manager"}
                id="lineManager"
                required
              >
                {({ value, onChange }) => (
                  <EmailSelect
                    value={value}
                    onChange={onChange}
                    xPageForm
                    className="react-select"
                    dataLoader={lineManagerDataLoader}
                  />
                )}
              </ControlledSelect>
            </div>

            <div className="hp-field">
              <ControlledSelect
                control={control}
                label={"Finance approver"}
                id="financeApprover"
                required
              >
                {({ value, onChange }) => (
                  <EmailSelect
                    value={value}
                    onChange={onChange}
                    xPageForm
                    className="react-select"
                    dataLoader={financeApproverDataLoader}
                    enabled={isLineManagerLoaded}
                  />
                )}
              </ControlledSelect>
            </div>

            <div className="hp-field hp-field--full">
              <FormGroup>
                <FormInput
                  label="Note to add to the e-mail (optional)"
                  name="additionalEmail"
                  control={control}
                  id="additionalEmail"
                  type="textarea"
                />
              </FormGroup>
            </div>
          </div>
        </SectionCard>

        {/* ═══ Submit ═══ */}
        <button type="submit" className="hp-submit-btn" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="hp-submit-btn__spinner" />
              Sending...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPaperPlane} />
              Send Request
            </>
          )}
        </button>
      </Form>
    </div>
  );
};

export default HomePage;
